import { Config } from '@shared/configs/config';
import * as moment from 'moment';

export const API_UTC_DATETIME_FORMAT = 'YYYY-MM-DD[T]HH:mm:ssZ';

export const parseApiDate = (value?: string | null): moment.Moment | null => {
  if (!value) return null;

  const normalizedValue = String(value).trim();
  if (!normalizedValue) return null;

  const legacyDate = moment(normalizedValue, Config.DATE_SERVER, true);
  if (legacyDate.isValid()) return legacyDate;

  const zonedDate = moment.parseZone(normalizedValue);
  if (zonedDate.isValid()) return zonedDate.local();

  const fallbackDate = moment(normalizedValue);
  return fallbackDate.isValid() ? fallbackDate : null;
};

export const formatApiUtcDateTime = (
  value?: string | moment.Moment | Date | null,
  options?: { endOfDay?: boolean }
): string | null => {
  if (!value) return null;

  const parsedDate = moment.isMoment(value) ? value.clone() : moment(value);
  if (!parsedDate.isValid()) return null;

  const normalizedDate = options?.endOfDay ? parsedDate.endOf('day') : parsedDate;
  return normalizedDate.utc().format(API_UTC_DATETIME_FORMAT);
};

export const formatApiDate = (value?: string | moment.Moment | Date | null): string | null => {
  if (!value) return null;

  const parsedDate = moment.isMoment(value) ? value.clone() : moment(value);
  if (!parsedDate.isValid()) return null;

  return parsedDate.format(Config.DATE_SERVER_YYMMDD);
};

export const normalizeApiDateValue = (value?: string | null): string | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;

  return formatApiUtcDateTime(value) ?? value;
};

export const normalizeApiDatePayload = <T>(payload: T): T => {
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeApiDatePayload(item)) as T;
  }

  if (!payload || typeof payload !== 'object') return payload;

  return Object.entries(payload as Record<string, unknown>).reduce(
    (acc, [key, value]) => {
      if (['termStart', 'termEnd', 'pauseStart', 'pauseEnd', 'startDate', 'endDate'].includes(key)) {
        acc[key] = normalizeApiDateValue(value as string | null | undefined);
        return acc;
      }

      acc[key] = normalizeApiDatePayload(value);
      return acc;
    },
    {} as Record<string, unknown>
  ) as T;
};

export const endDateHelper = (termEnd) => {
  const parsedDate = parseApiDate(termEnd);
  return parsedDate ? parsedDate.set('second', 59).utc().format(API_UTC_DATETIME_FORMAT) : null;
};
